/**
 * PLATFORM-UX — SA Platform shell with five L2 groups (Access / AI Supply /
 * Estate / Edge Lab / Evidence). Tab bodies stay Live desks in sa-*.js.
 *
 * Soft≠Live Page217: dual shell_plane ∥ write_plane via GET /sa/status.
 */
import { useState, useEffect, useMemo } from "react";
import { AnimatePresence } from "framer-motion";
import { html, M, SOFT, FADE, go, Alert, Ico } from "./ui.js";
import { path, useApi } from "./api.js";
import { useSession } from "./session.js";
import { confirmLeaveSeat } from "./shell-wayfind.js";
import { formatUserInstant } from "./time.js";

import { Caps } from "./sa-caps.js";
import { PlatformModels } from "./sa-models.js";
import { AiToolsDesk } from "./ai-tools-desk.js";
import { Team } from "./sa-team.js";
import { Tenants } from "./sa-tenants.js";
import { Custody } from "./sa-custody.js";
import { Factory } from "./sa-factory.js";
import { Audit } from "./sa-audit.js";
import { FLEET_HEALTH_PATH } from "./sa-ops.js";
import { Vault } from "./sa-vault.js";

/** PLATFORM-UX L2 group order — visible ≤5. */
export const PLATFORM_L2_GROUPS = Object.freeze([
  "Access",
  "AI Supply",
  "Estate",
  "Edge Lab",
  "Evidence",
]);

const TABS = [
  { id: "caps", label: "Capabilities", group: "Access" },
  { id: "team", label: "Staff", group: "Access" },
  { id: "models", label: "Models", group: "AI Supply" },
  { id: "ai-tools", label: "Skills", group: "AI Supply" },
  { id: "tenant", label: "Tenants", group: "Estate" },
  { id: "custody", label: "Custody", group: "Estate" },
  { id: "factory", label: "Factory", group: "Estate" },
  { id: "edge-lab", label: "Edge Lab", group: "Edge Lab" },
  { id: "audit", label: "Audit", group: "Evidence" },
  { id: "vault", label: "Backup export", group: "Evidence" },
];

/** Soft≠Live Page217: missing contract op must not crash the desk. */
function safeSaShellPath(op, opts) {
  try {
    return path(op, opts);
  } catch {
    if (op === "get__sa_status") return "/sa/status";
    return "";
  }
}

/** Path-free operator copy for Platform shell. */
export function softSaShellErrorOf(err) {
  const n = String(err == null ? "" : err).trim();
  const low = n.toLowerCase();
  if (!low) return "That action could not be completed.";
  if (/\b403\b|forbidden|seat_gated|not_authorized|denied|insufficient|floor/.test(low)) {
    return "Your account cannot open Platform. Ask a root SA if you need access.";
  }
  if (/\b401\b|session|unauthorized|missing bearer/.test(low)) {
    return "Sign in again to continue.";
  }
  if (/\b404\b|not found/.test(low)) {
    return "That Platform seat is not available.";
  }
  if (/\b429\b|too many/.test(low)) {
    return "Too many tries — wait a moment, then retry.";
  }
  if (/\b503\b|unavailable|disabled/.test(low)) {
    return "Platform is not ready right now.";
  }
  if (/network|failed to fetch|timeout/.test(low)) {
    return "Network issue — try again in a moment.";
  }
  if (/\/api\/|\/sa\/|GET |POST |status_path|http/.test(low)) {
    return "That action could not be completed.";
  }
  if (n.length > 160) return n.slice(0, 157) + "…";
  return n;
}

/** Pure: seat chips from Live Platform shell posture (Page217). Fail-closed · === true only. */
export function saShellSeatOf(post) {
  const p = post && typeof post === "object" ? post : {};
  const lim = (p.limits && typeof p.limits === "object") ? p.limits : {};
  const tabMax = Number(lim.tab_paint_max);
  const shell = p.shell_plane_allowed === true;
  return {
    enabled: p.enabled === true && shell,
    armed: p.armed === true,
    shellAllowed: shell,
    writeAllowed: p.write_plane_allowed === true,
    navAllowed: p.nav_allowed === true,
    reReadAllowed: p.re_read_allowed === true,
    role: String(p.principal_role || ""),
    detail: p.detail || null,
    selectedTenant: String(p.selected_tenant || p.tenant_scope || "").trim(),
    tabMatrix: (p.tab_matrix && typeof p.tab_matrix === "object") ? p.tab_matrix : null,
    tabPaintMax:
      Number.isFinite(tabMax) && tabMax > 0 ? Math.floor(tabMax) : 12,
  };
}

/** Live countdown from /auth/me — never invent a brochure TTL. */
function GrantExpiry() {
  const { me } = useSession();
  const until = me && me.saGrantedUntil;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!until) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [until]);
  let face = "—";
  let tone = "var(--fg-3)";
  let sub = "No SA grant expiry on this principal";
  if (until) {
    const left = Math.max(0, until * 1000 - now);
    if (left <= 0) {
      face = "expired";
      tone = "var(--coral)";
      sub = "Capabilities drop on the next request";
    } else {
      const d = Math.floor(left / 864e5);
      const h = Math.floor((left % 864e5) / 36e5);
      const m = Math.floor((left % 36e5) / 6e4);
      face = d > 0 ? `${d}d ${String(h).padStart(2, "0")}h` : `${h}h ${String(m).padStart(2, "0")}m`;
      tone = left < 7 * 864e5 ? "var(--amber)" : "var(--aqua)";
      sub = "Until " + formatUserInstant(until, { seconds: false, zoneName: true });
    }
  } else if (me && me.role === "superadmin") {
    face = "root";
    tone = "var(--aqua)";
    sub = "Root SA — singular, not time-boxed by staff grant TTL";
  }
  return html`
    <div className="glass sa-clock" style=${{ borderRadius: "var(--r-md)", padding: "12px 16px", flex: "0 0 auto" }}>
      <div className="z">
        <div className="sa-clock__k">Grant expires</div>
        <div className="sa-clock__v" style=${{ fontSize: "var(--t-h3)", color: tone }}>${face}</div>
        <div style=${{ fontSize: "var(--t-micro)", color: "var(--fg-4)", marginTop: 4, maxWidth: 220, overflowWrap: "anywhere" }}>${sub}</div>
      </div>
    </div>`;
}

export default function SA({ sub, toast }) {
  const { me } = useSession();
  const tab = TABS.find((t) => t.id === sub) ? sub : "caps";
  const role = String((me && me.role) || "");
  const isRoot = role === "superadmin";
  const staffish = role === "sa_staff";
  const posture = useApi(safeSaShellPath("get__sa_status"));
  const post = posture.data || {};
  const seat = saShellSeatOf(
    posture.loading || !posture.data
      ? { enabled: false, principal_role: role, limits: { tab_paint_max: 12 } }
      : post,
  );
  const setTab = (id) => {
    if (seat.shellAllowed !== true) {
      toast && toast("Platform shell is closed on your account", "warn");
      return;
    }
    go("/sa/" + id);
  };
  const tabsPaint = useMemo(() => {
    const capped = TABS.slice(0, seat.tabPaintMax);
    const matrix = seat.tabMatrix;
    if (!matrix) return [];
    return capped.filter((t) => {
      const row = matrix[t.id];
      if (!row || typeof row !== "object") return false;
      return row.visible === true;
    });
  }, [seat.tabPaintMax, seat.tabMatrix]);
  /* CAP-COMPLETE: if deep-link tab is sealed, land on first visible seat. */
  useEffect(() => {
    if (seat.shellAllowed !== true || !tabsPaint.length) return;
    if (!tabsPaint.some((t) => t.id === tab)) {
      go("/sa/" + tabsPaint[0].id);
    }
  }, [seat.shellAllowed, tabsPaint, tab]);
  const groupSeen = useMemo(() => new Set(tabsPaint.map((t) => t.group)), [tabsPaint]);
  const groups = useMemo(
    () => [
      ...PLATFORM_L2_GROUPS.filter((g) => groupSeen.has(g)),
      ...[...groupSeen].filter((g) => !PLATFORM_L2_GROUPS.includes(g)),
    ],
    [groupSeen],
  );
  const activeGroup = (TABS.find((t) => t.id === tab) || {}).group || groups[0] || "Access";
  const tenantFace = seat.selectedTenant
    || String((me && (me.selected_tenant || me.tenant_id || me.owner_admin)) || "").trim();

  const reReadPosture = () => {
    if (seat.reReadAllowed !== true) {
      toast && toast("Re-read is closed for this account", "warn");
      return;
    }
    posture.reload && posture.reload();
    toast && toast("Platform status refreshed", "go");
  };

  const leave = (target) => confirmLeaveSeat({ fromRoute: "/sa/" + tab, targetPath: target });

  const lede = isRoot
    ? "You are the singular root SA. Global management lives here. Admins are tenants under you; users hang on each admin tree. There is no second SA to register."
    : staffish
      ? "You are signed in as delegated staff. Every capability you hold is time-boxed and every action you take here writes an audit row that names you. You are not a second SA — parents and tenants stay on the admin tree."
      : "Platform controls for the singular SA grant on this deployment. Capability checks still gate every write — the chrome does not invent reach you do not hold.";

  if (!me) {
    return html`
      <div className="sheet sa-desk sa-desk--liquid desk-craft"
        data-cmc-page="page217-sa-shell" data-cmc-residual="page217-sa-shell" data-cmc-shell="platform-ux">
        <div className="wrap">
          <${Alert} tone="warn" title="No session" say="Sign in to open Platform."/>
        </div>
      </div>`;
  }
  if (posture.loading && !posture.data) {
    return html`
      <div className="sheet sa-desk sa-desk--liquid desk-craft"
        data-cmc-page="page217-sa-shell" data-cmc-residual="page217-sa-shell" data-cmc-shell="platform-ux">
        <div className="wrap">
          <p className="note">Confirming Platform seat…</p>
        </div>
      </div>`;
  }

  return html`
    <div className="sheet sa-desk sa-desk--liquid desk-craft house-rack"
      data-cmc-page="page217-sa-shell" data-cmc-residual="page217-sa-shell" data-cmc-shell="platform-ux"
      data-cmc-plat="plat3-1" data-cmc-cap="cap-complete">
      <div className="wrap">
        <div className="sa-desk__rail">
          <div style=${{ flex: "1 1 auto", minWidth: 0 }}>
            <p className="eyebrow">Platform · five groups</p>
            <h1 className="ph sa-desk__title">Platform</h1>
            ${tenantFace ? html`<${Alert} tone="go" title="Selected tenant"
              say=${"Working scope · " + tenantFace + ". Tenant Settings stay under Settings primary."}/>` : null}
            <${Alert} tone="info" title="Platform seats"
              say=${`Shell ${seat.shellAllowed ? "open" : "sealed"} · write ${seat.writeAllowed ? "open" : "sealed"} · ${seat.role || role || "—"}.`}/>
            ${seat.shellAllowed !== true ? html`<${Alert} tone="warn" title="Shell sealed"
              say=${softSaShellErrorOf(seat.detail || posture.error) || "Platform shell needs root SA or delegated staff."}/>` : null}
            ${seat.shellAllowed === true && seat.writeAllowed !== true ? html`<${Alert} tone="info" title="Write sealed"
              say="Staff grant writes and tab changes need capability grants on your account."/>` : null}
            <p className="psub">${lede}
              {" "}Security posture under <button type="button" className="btn btn--text btn--sm" style=${{ padding: 0, verticalAlign: "baseline" }}
                onClick=${() => leave("/sa/caps")}>Access · Capabilities</button>
              {" · "}Machine health under <button type="button" className="btn btn--text btn--sm" style=${{ padding: 0, verticalAlign: "baseline" }}
                onClick=${() => leave(FLEET_HEALTH_PATH)}>Machine · health</button>.
            </p>
          </div>
          <div className="sa-desk__aside">
            <${GrantExpiry}/>
            <button type="button" className="btn btn--ghost btn--sm" style=${{ marginTop: 8 }}
              disabled=${seat.reReadAllowed !== true || posture.loading} onClick=${reReadPosture}>
              <${Ico} d="refresh" w=${13}/> Re-read</button>
          </div>
        </div>

        <div className="sa-desk__kpis" aria-label="SA shell dual buses">
          <div className="sa-desk__kpi" data-on=${seat.shellAllowed ? "1" : "0"}>
            <span>Shell</span>
            <b>${seat.shellAllowed ? "open" : "sealed"}</b>
            <em>nav · desks</em>
          </div>
          <div className="sa-desk__kpi" data-on=${seat.writeAllowed ? "1" : "0"}>
            <span>Write</span>
            <b>${seat.writeAllowed ? "open" : "sealed"}</b>
            <em>grants · mutate</em>
          </div>
          <div className="sa-desk__kpi" data-on=${seat.armed ? "1" : "0"}>
            <span>Armed</span>
            <b>${seat.armed ? "yes" : "no"}</b>
            <em>status</em>
          </div>
          <div className="sa-desk__kpi" data-on="1">
            <span>Role</span>
            <b>${seat.role || role || "—"}</b>
            <em>principal</em>
          </div>
        </div>

        ${seat.shellAllowed === true && groups.length ? html`
        <nav className="sa-desk__l2" aria-label="Platform groups" style=${{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
          ${groups.map((g) => html`
            <button key=${g} type="button" className="chip"
              data-tone=${activeGroup === g ? "go" : "ai"}
              onClick=${() => {
                const first = tabsPaint.find((t) => t.group === g);
                if (first) setTab(first.id);
              }}>${g}</button>`)}
        </nav>` : null}

        ${seat.shellAllowed === true && tabsPaint.length ? html`
        <div className="glass sa-tabs" data-on="1" aria-label=${activeGroup + " seats"}>
          ${tabsPaint.filter((t) => t.group === activeGroup).map((t) => html`
            <button key=${t.id} type="button" data-on=${tab === t.id ? "1" : "0"}
              onClick=${() => setTab(t.id)}>
              ${tab === t.id && html`<${M.span} layoutId="sa-pill" className="nav-pill" transition=${SOFT}/>`}
              <span className="nav__label">${t.label}</span>
            </button>`)}
        </div>` : null}

        ${seat.shellAllowed === true ? html`
        <${AnimatePresence} mode="wait">
          <${M.div} key=${tab} initial=${{ opacity: 0, y: 10 }} animate=${{ opacity: 1, y: 0 }}
              exit=${{ opacity: 0, y: -8 }} transition=${FADE}
              style=${{ display: "flex", flexDirection: "column", gap: 14 }}>
            ${tab === "caps" && html`<${Caps}/>`}
            ${tab === "models" && html`<${PlatformModels} toast=${toast}/>`}
            ${tab === "ai-tools" && html`<${AiToolsDesk} toast=${toast}/>`}
            ${tab === "edge-lab" && html`<div data-cmc-plat="plat3-1">
              <${Alert} tone="info" title="Edge Lab"
                say="Technical lab seats live here — not on the tenant topbar. Fleet OTA campaigns stay on OTA."/>
              <div className="btn-row" style=${{ flexWrap: "wrap", gap: 8, marginTop: 12 }}>
                <button type="button" className="btn btn--go btn--sm" onClick=${() => leave("/edge/estate")}>Edge estate</button>
                <button type="button" className="btn btn--ghost btn--sm" onClick=${() => leave("/edge/skills")}>Skills warehouse</button>
                <button type="button" className="btn btn--ghost btn--sm" onClick=${() => leave("/edge/board")}>Board · Pin</button>
                <button type="button" className="btn btn--ghost btn--sm" onClick=${() => leave("/edge/bench")}>Bench</button>
                <button type="button" className="btn btn--ghost btn--sm" onClick=${() => leave("/ota")}>OTA primary</button>
                <button type="button" className="btn btn--ghost btn--sm" onClick=${() => leave("/machine")}>Machine</button>
              </div>
              <p className="note" style=${{ marginTop: 12 }}>Machine health focus stays on the managed list — Platform only bridges here.</p>
            </div>`}
            ${tab === "team" && html`<${Team} toast=${toast}/>`}
            ${tab === "tenant" && html`<${Tenants} toast=${toast}/>`}
            ${tab === "custody" && html`<${Custody} toast=${toast}/>`}
            ${tab === "factory" && html`<${Factory} toast=${toast}/>`}
            ${tab === "audit" && html`<${Audit}/>`}
            ${tab === "vault" && html`<${Vault} toast=${toast}/>`}
          <//>
        <//>
        ` : html`
          <${Alert} tone="warn" title="Platform body sealed"
            say=${softSaShellErrorOf(seat.detail || posture.error)
              || "Sign in as root SA or delegated staff for the Platform shell."}/>
        `}
      </div>
    </div>`;
}
